import axios, { AxiosHeaders, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { handleSessionExpired, isSessionExpiryApplicable } from '@shared/utils/authSession';
import { isTokenExpired } from '@shared/utils/jwt';
import { applyLanguageHeader } from '@shared/api/languageHeader';

declare module 'axios' {
  interface AxiosRequestConfig {
    skipRetry?: boolean;
  }
}

const apiBaseFromEnv = (import.meta.env.VITE_API_BASE_URL ?? '').trim();
const API_BASE_URL = apiBaseFromEnv !== '' ? apiBaseFromEnv : '/api/v1';

const DEV_AUTH_TOKEN =
  import.meta.env.DEV
    ? import.meta.env.VITE_DEV_AUTH_TOKEN || 'dev-local-token'
    : undefined;

const DEV_AUTH_USERNAME =
  import.meta.env.DEV ? import.meta.env.VITE_DEV_AUTH_USERNAME : undefined;
const DEV_AUTH_EMPLOYEE_ID =
  import.meta.env.DEV ? import.meta.env.VITE_DEV_AUTH_EMPLOYEE_ID : undefined;
const hasLoggedRequestInfo = false;

// Обновление токена — single-flight на всю страницу.
//
// Сервер ротирует пару: выпуская новую, он отзывает предъявленный
// refresh-токен. Значит два параллельных обновления одним и тем же токеном —
// это гарантированный 401 у второго и выброс оператора на /login. Раньше гонка
// была безобидна (старый токен оставался жив), теперь нет.
let inFlightRefresh: Promise<string> | null = null;

function refreshAccessTokenOnce(refreshToken: string): Promise<string> {
  if (inFlightRefresh) return inFlightRefresh;

  // Отдельный инстанс без перехватчиков — иначе обновление зациклится само на себе.
  // Таймаут обязателен: общее обещание должно ЗАВЕРШИТЬСЯ в любом случае.
  // Зависшее соединение без таймаута оставило бы inFlightRefresh навсегда, и
  // страница перестала бы обновлять токен вовсе — до перезагрузки.
  const refreshClient = axios.create({
    baseURL: API_BASE_URL,
    headers: { 'Content-Type': 'application/json' },
    timeout: 30000, // как у apiClient
  });

  inFlightRefresh = refreshClient
    .post('/auth/refresh', { refresh_token: refreshToken })
    .then(({ data }) => {
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      return data.access_token as string;
    })
    .finally(() => {
      inFlightRefresh = null;
    });

  return inFlightRefresh;
}

// Конфигурация retry
interface RetryConfig {
  retries: number;
  retryDelay: number;
  retryCondition?: (error: AxiosError) => boolean;
}

/**
 * Методы, которые можно безопасно повторить.
 *
 * Намеренно ТОЛЬКО чтение. PUT и DELETE идемпотентны по RFC, но не в этой
 * системе: связи пишутся через delete+insert (см. tools/roundtrip/README.md),
 * а повторный DELETE отдаёт оператору ложный 404.
 */
const RETRIABLE_METHODS = new Set(['get', 'head', 'options']);

const isRetriableMethod = (method?: string): boolean =>
  RETRIABLE_METHODS.has((method ?? 'get').toLowerCase());

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  retries: 3,
  retryDelay: 1000, // 1 секунда
  retryCondition: (error: AxiosError) => {
    // Сетевая ошибка и 5xx НЕ означают, что сервер запрос не выполнил: ответ мог
    // потеряться уже после коммита. Для записи повтор = второе списание с
    // лицевого счёта (POST /customers/{id}/balance), вторая задача, второй акт.
    // Идемпотентности на этих ручках нет (единственный Idempotency-Key —
    // intake_schema_handler.go), поэтому повторяем только чтение.
    if (!isRetriableMethod(error.config?.method)) {
      return false;
    }
    if (!error.response) {
      return true; // Сетевая ошибка
    }
    const status = error.response.status;
    return status >= 500 && status < 600; // Серверные ошибки
  },
};

// Централизованное логирование ошибок
function logApiError(error: AxiosError, context?: string): void {
  // ✅ ИСПРАВЛЕНИЕ: Улучшенная обработка таймаутов и сетевых ошибок
  const isTimeout = error.code === 'ECONNABORTED' || error.message.includes('timeout');
  const isNetworkError = !error.response && error.request;
  const isCanceled = error.code === 'ERR_CANCELED' || error.message === 'canceled';
  
  
  // Не логируем отмененные запросы - это нормальное поведение React Query
  if (isCanceled) {
    return;
  }

  const errorInfo = {
    timestamp: new Date().toISOString(),
    context: context || 'API Request',
    method: error.config?.method?.toUpperCase(),
    url: error.config?.url,
    fullUrl: error.config ? `${error.config.baseURL || API_BASE_URL}${error.config.url}` : undefined,
    status: error.response?.status,
    statusText: error.response?.statusText,
    message: error.message,
    code: error.code,
    isTimeout,
    isNetworkError,
    timeout: error.config?.timeout,
    responseData: error.response?.data,
    requestData: error.config?.data,
    hasRequest: !!error.request,
    hasResponse: !!error.response,
    retryCount: (error.config as any)?._retryCount || 0,
  };

  if (import.meta.env.DEV) {
    // ✅ Улучшенное логирование: выводим детали ошибки для диагностики
    const responseDataString = errorInfo.responseData ? JSON.stringify(errorInfo.responseData, null, 2) : undefined;
    
    console.error('[API Error]', {
      ...errorInfo,
      responseDataString,
    });
    
    // ✅ ДОПОЛНИТЕЛЬНО: Выводим responseData отдельно для удобства чтения
    if (responseDataString) {
      console.error('[API Error] Response Data:', responseDataString);
    }

    // Дополнительное предупреждение для таймаутов
    if (isTimeout) {
      console.warn(
        '[API Timeout]',
        `Запрос превысил таймаут (${error.config?.timeout}ms). ` +
        `Возможно, SNMP запрос занимает слишком много времени. ` +
        `URL: ${error.config?.url}`
      );
    }

    // Дополнительная информация для сетевых ошибок
    if (isNetworkError) {
      console.warn(
        '[API Network Error]',
        `Сетевая ошибка при запросе: ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
        `BaseURL: ${API_BASE_URL}`,
        `Full URL: ${error.config ? `${error.config.baseURL || API_BASE_URL}${error.config.url}` : 'unknown'}`,
        `Error code: ${error.code || 'unknown'}`,
        `Message: ${error.message}`
      );
    }
  }

  // В production можно отправлять в систему мониторинга
  if (import.meta.env.PROD && error.response?.status && error.response.status >= 500) {
    // TODO: Интеграция с системой мониторинга (Sentry, LogRocket и т.д.)
  }
}

// Создать Axios instance

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 секунд
});

// Request interceptor - добавить JWT токен и проверить истечение
apiClient.interceptors.request.use(
  (config) => {

    let token = localStorage.getItem('access_token');
    const headers =
      config.headers instanceof AxiosHeaders
        ? config.headers
        : new AxiosHeaders(config.headers ?? {});

    config.headers = headers;

    // Язык экрана — через общего производителя (`shared/api/languageHeader`), а не копией:
    // через ЭТОТ инстанс ходят задачи, и именно он остался без заголовка в первой
    // редакции цикла 97.
    applyLanguageHeader(config);

    // Проверить истечение токена перед запросом.
    // Условие «а вход ли это?» тут не дублируется: применимость политики
    // определяет воронка (authSession), клиент только спрашивает её.
    if (token && isTokenExpired(token)) {
      if (isSessionExpiryApplicable(config.url)) {
        // Токен истек, обработаем истечение сессии
        // Используем setTimeout чтобы не блокировать выполнение
        setTimeout(() => {
          handleSessionExpired('expired', {
            showNotification: true,
            redirectToLogin: true,
            requestUrl: config.url,
          });
        }, 0);
        // Отменить запрос
        return Promise.reject(new Error('Token expired'));
      }

      // Это сам запрос входа: отменять нечего, мёртвый токен просто не прикладываем.
      token = null;
    }

    if (!token && DEV_AUTH_TOKEN) {
      token = DEV_AUTH_TOKEN;

      if (DEV_AUTH_EMPLOYEE_ID) {
        headers.set('X-Dev-Employee-Id', DEV_AUTH_EMPLOYEE_ID);
      }
      if (DEV_AUTH_USERNAME) {
        headers.set('X-Dev-Username', DEV_AUTH_USERNAME);
      }
    }

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - обработка ошибок с retry-логикой
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean; _retryCount?: number };
    
    // Пропускаем отмененные запросы (ERR_CANCELED) - это нормальное поведение React Query
    const isCanceled = error.code === 'ERR_CANCELED' || error.message === 'canceled';
    if (isCanceled) {
      return Promise.reject(error);
    }

    // Логирование ошибки
    logApiError(error);

    // Если 401 и это не повторный запрос
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Попытаться обновить токен
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken && !isTokenExpired(refreshToken)) {
          // Обновление идёт ОДНО на всю страницу: сервер ротирует refresh-токен
          // и отзывает предъявленный, поэтому второе параллельное обновление
          // тем же токеном получило бы 401 и выбросило оператора. Экран,
          // отправивший несколько запросов разом, ловит несколько 401 —
          // случай штатный, а не экзотический.
          const accessToken = await refreshAccessTokenOnce(refreshToken);

          // Повторить оригинальный запрос
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        } else {
          // Refresh token истек или отсутствует
          handleSessionExpired('refresh_failed', {
            showNotification: true,
            redirectToLogin: true,
            requestUrl: originalRequest?.url,
          });
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // 503 на обновлении = сервер не смог проверить чёрный список отозванных
        // токенов (fail-closed), а НЕ «токен негодный». Выбрасывать оператора и
        // чистить валидный refresh-токен из-за секундного сбоя Redis нельзя:
        // выброс пережил бы сам сбой. Сессию не трогаем, ошибку отдаём наверх.
        if (axios.isAxiosError(refreshError) && refreshError.response?.status === 503) {
          return Promise.reject(refreshError);
        }
        // Если обновление токена не удалось - обработать истечение сессии
        handleSessionExpired('refresh_failed', {
          showNotification: true,
          redirectToLogin: true,
          requestUrl: originalRequest?.url,
        });
        return Promise.reject(refreshError);
      }
    }

    // Если 401 и это повторный запрос (refresh не помог)
    if (error.response?.status === 401 && originalRequest._retry) {
      handleSessionExpired('unauthorized', {
        showNotification: true,
        redirectToLogin: true,
        requestUrl: originalRequest?.url,
      });
      return Promise.reject(error);
    }

    // Retry-логика для сетевых ошибок и серверных ошибок
    if (originalRequest && !originalRequest.skipRetry && DEFAULT_RETRY_CONFIG.retryCondition?.(error)) {
      const retryCount = originalRequest._retryCount || 0;

      if (retryCount < DEFAULT_RETRY_CONFIG.retries) {
        originalRequest._retryCount = retryCount + 1;

        // Экспоненциальная задержка: 1s, 2s, 4s
        const delay = DEFAULT_RETRY_CONFIG.retryDelay * Math.pow(2, retryCount);

        await new Promise((resolve) => setTimeout(resolve, delay));

        return apiClient(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);
